import { Link } from "react-router-dom"
import {
  CreditCard, Wallet, Banknote, ShieldCheck, Info, AlertCircle, CheckCircle2,
  RefreshCw, ArrowRight, Receipt, BookOpen, FileQuestion,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import { Alert } from "../../components/ui/Alert"
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "../../components/ui/Table"
import { formatNaira } from "../../lib/constants"
import { usePublicFees } from "../../hooks/usePublic"

const levels = [
  { key: "ALL", label: "All Levels" },
  { key: "NCE_1", label: "NCE 1" },
  { key: "NCE_2", label: "NCE 2" },
  { key: "NCE_3", label: "NCE 3" },
]

const gateways = [
  {
    name: "Paystack",
    icon: CreditCard,
    description:
      "Paystack is our recommended gateway for card and bank payments. It accepts all major Nigerian debit cards (Verve, Mastercard, Visa) and supports instant bank transfers from over 15 banks. Transactions are secured with 3D Secure and tokenised for repeat payments.",
  },
  {
    name: "Flutterwave",
    icon: Wallet,
    description:
      "Flutterwave offers flexible payment options including card, USSD, mobile money and bank account transfers. It is ideal for students who prefer USSD codes or mobile money. The platform provides real-time transaction receipts and 24/7 support.",
  },
  {
    name: "Monnify",
    icon: Banknote,
    description:
      "Monnify specialises in direct bank-account transfers with a unique virtual account number generated per transaction. Funds are confirmed instantly and reconciled automatically with the Student Portal — perfect for students without debit cards.",
  },
]

const instructions = [
  "Log in to the Student Portal and navigate to the Payments section.",
  "Click 'Initiate Payment' and select the fee type you wish to pay (e.g. tuition, registration).",
  "Choose your preferred gateway — Paystack, Flutterwave or Monnify.",
  "Review the amount and click 'Proceed' to be redirected to the gateway's secure payment page.",
  "Complete the payment on the gateway. You will be redirected back to the portal automatically.",
  "Print or download the payment receipt. The transaction will reflect as PAID on your portal within minutes.",
]

export default function Fees() {
  const { data, isLoading } = usePublicFees()
  const fees = data?.fees || []

  function groupByLevel(level: string) {
    return fees.filter(f => f.level === level || (level === "ALL" && (f.level === "ALL" || !f.level)))
  }

  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Fees" }]}
        subtitle="Fees & Payments"
        title="Transparent Fee Structure for Every Student"
        description="Review the official fee schedule for the Nigeria Certificate in Education (NCE) programme, learn about accepted payment methods and read our refund policy."
      />

      {/* Fee structure */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="success" className="mb-3">2025/2026 Session</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">Fee Structure</h2>
            <p className="text-ink-500 dark:text-ink-400 mt-3 max-w-2xl mx-auto">
              All fees are denominated in Nigerian Naira (₦). The College accepts payments through Paystack, Flutterwave and Monnify. Fees are reviewed annually.
            </p>
          </div>

          {isLoading ? (
            <Card className="border-0 shadow-md">
              <CardContent className="py-16 text-center text-sm text-ink-500 dark:text-ink-400">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-700 mx-auto mb-4" />
                Loading fee structure...
              </CardContent>
            </Card>
          ) : fees.length === 0 ? (
            <Card className="border-0 shadow-md">
              <CardContent className="py-16 text-center">
                <Receipt className="w-12 h-12 text-ink-400 mx-auto mb-3" />
                <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-2">Fee Schedule Being Updated</h3>
                <p className="text-sm text-ink-500 dark:text-ink-400 max-w-md mx-auto">
                  The official fee schedule for the current session is being updated. Please check back shortly or contact the Bursary for assistance.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-8">
              {levels.map(level => {
                const rows = groupByLevel(level.key)
                const total = rows.reduce((sum, r) => sum + (r.amount || 0), 0)
                if (rows.length === 0) return null
                return (
                  <Card key={level.key} className="border-0 shadow-md overflow-hidden">
                    <CardHeader className="bg-brand-50 dark:bg-brand-900/20 border-b border-brand-100 dark:border-brand-900/40">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-brand-900 dark:text-brand-200">{level.label}</CardTitle>
                          <CardDescription>{rows.length} fee item(s)</CardDescription>
                        </div>
                        <div className="text-right">
                          <div className="text-xs uppercase tracking-wider text-ink-500 dark:text-ink-400">Sub-total</div>
                          <div className="font-display text-xl font-bold text-brand-800 dark:text-brand-300">{formatNaira(total)}</div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableHeader>Fee Type</TableHeader>
                            <TableHeader>Description</TableHeader>
                            <TableHeader className="text-right">Amount</TableHeader>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((f, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-semibold">{f.fee_type?.replace(/_/g, " ")}</TableCell>
                              <TableCell className="text-ink-600 dark:text-ink-400">{f.description || "—"}</TableCell>
                              <TableCell className="text-right font-semibold">{formatNaira(f.amount || 0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}

          <Alert variant="info" className="mt-8">
            <div className="flex items-start gap-2 text-sm">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Fees are subject to change. Late registration attracts an additional fee of ₦5,000. Hostel accommodation is optional and billed separately. Acceptance fee is non-refundable once paid.
              </span>
            </div>
          </Alert>
        </div>
      </section>

      {/* Payment methods */}
      <section className="py-16 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="warning" className="mb-3">Accepted Gateways</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">Payment Methods</h2>
            <p className="text-ink-500 dark:text-ink-400 mt-3 max-w-2xl mx-auto">
              We support three secure online payment gateways. Choose the option most convenient for you — all transactions are encrypted and reconcile automatically with your portal.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {gateways.map(g => (
              <Card key={g.name} className="card-lift border-0 shadow-md">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-xl bg-brand-50 dark:bg-brand-900/40 grid place-items-center mb-4">
                    <g.icon className="w-6 h-6 text-brand-700 dark:text-brand-400" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-2">{g.name}</h3>
                  <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">{g.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Payment instructions */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <Badge variant="success" className="mb-3">Step-by-Step</Badge>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100 mb-5 leading-tight">
                How to Pay Your Fees
              </h2>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-6">
                Follow these six steps to complete any fee payment on the Student Portal. The process is identical for Paystack, Flutterwave and Monnify — only the payment screen differs.
              </p>
              <Link to="/portal/login">
                <Button className="bg-brand-800 dark:bg-brand-700">
                  Go to Student Portal <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <Card className="border-0 shadow-md">
              <CardContent className="pt-6">
                <ol className="space-y-5">
                  {instructions.map((step, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-brand-700 text-white grid place-items-center font-display font-bold text-sm shrink-0">
                        {i + 1}
                      </div>
                      <p className="text-sm text-ink-700 dark:text-ink-300 leading-relaxed pt-1">{step}</p>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Refund policy */}
      <section className="py-16 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-0 shadow-md">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-brand-50 dark:bg-brand-900/40 grid place-items-center">
                    <RefreshCw className="w-6 h-6 text-brand-700 dark:text-brand-400" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100">Refund Policy</h3>
                </div>
                <div className="space-y-3 text-sm text-ink-600 dark:text-ink-400 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" />
                    <span>Full refund of tuition if a student officially withdraws within two (2) weeks of resumption and has not registered for any course.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" />
                    <span>50% refund of tuition if withdrawal occurs between the third and sixth week of the semester.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" />
                    <span>No refund of tuition after the sixth week of the semester. Acceptance, registration and examination fees are non-refundable.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" />
                    <span>Overpayments and failed-but-debited transactions are refunded within 14 working days of verification by the Bursary.</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-gold-50 dark:bg-gold-900/30 grid place-items-center">
                    <FileQuestion className="w-6 h-6 text-gold-700 dark:text-gold-400" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100">Fee FAQ</h3>
                </div>
                <div className="space-y-4 text-sm">
                  <div>
                    <div className="font-semibold text-ink-900 dark:text-ink-100 mb-1">Can I pay in instalments?</div>
                    <p className="text-ink-600 dark:text-ink-400 leading-relaxed">Yes — up to two instalments per session: 60% in semester 1 and 40% before semester 2 examinations. Request formally at the Bursary.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-ink-900 dark:text-ink-100 mb-1">What if my payment fails but money is debited?</div>
                    <p className="text-ink-600 dark:text-ink-400 leading-relaxed">Auto-reversal typically happens within 24 hours. If unresolved after 48 hours, contact the Bursary with your transaction reference.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-ink-900 dark:text-ink-100 mb-1">Are there scholarships?</div>
                    <p className="text-ink-600 dark:text-ink-400 leading-relaxed">Yes. Government and private scholarships are advertised on the News page when applications open.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Alert variant="warning" className="mt-8">
            <div className="flex items-start gap-2 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                <strong>Security Notice:</strong> Never pay fees into individual staff bank accounts or through third-party agents. All official payments must be made through the Student Portal gateways. The College will not be liable for payments made outside official channels.
              </span>
            </div>
          </Alert>

          <div className="mt-8 text-center">
            <Link to="/contact">
              <Button variant="outline">
                <ShieldCheck className="w-4 h-4 mr-2" /> Contact the Bursary
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
